import { useEffect, useState } from "react";
import {
  useCustom,
  useCustomMutation,
  useOne,
  useUpdate,
  useGetLocale,
  useTranslate,
} from "@refinedev/core";
import {
  DateField,
  PlusCircleSolidIcon,
  ActionBar,
  Button,
  FieldShell,
  FormCard,
  Modal,
  ReadField,
  StatusBadge,
  Tile,
  TrashIcon,
  InvoiceIcon,
  type Column,
} from "@fawkes/ui";

import { formatDate, formatEuro, todayIso } from "../i18n/format";
import { useModuleAvailability } from "../api/modules";
import { EntingenTab } from "./EntingenTab";
import { BehandelingTab } from "./BehandelingTab";
import { MedicijnTab } from "./MedicijnTab";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

// products-migration Story 2.4: the product detail screen — opens DIRECTLY EDITABLE (Jmix parity:
// the Jmix "Product wijzigen" dialog opens with all fields editable, footer "Verwijderen · Annuleren ·
// Opslaan"). The four plain-column tiles (Basisinformatie, Classificatie & codes, Voorraad, Boekhouding)
// plus the Metadata tile render as an editable form on open; the Prijzen/Voorraadmutaties surfaces have
// their own add/remove/booking dialogs (Epics 3–4) which work independently of the form edit state.
//
// This mirrors the shipping Jmix ProductDetailDialog idiom (capture 02: the whole dialog edits, then
// "Annuleren · Opslaan"). The save fans the four tiles into ONE `PUT /api/producten/{id}` (Story 2.3),
// carrying the optimistic-lock `version`; a 409 surfaces a "gewijzigd door iemand anders" message
// (matching the BasisTile save/version/409 idiom). Dropdowns are fed by `GET /api/producten/opties`.
//
// "Annuleren" reverts unsaved edits (re-seeds the form from the last-loaded product) and navigates back,
// matching the Jmix dialog "Annuleren" (discard + close). A read-only render path is kept for the brief
// window before the editable form is seeded (and as a defensive fallback).
//
// Stock masking (FR12): when `stockMasked` the current-stock value is server-withheld, so even in edit
// mode "Huidige voorraad" stays a read-only badge — a masked operator can never write it.
//
// All URLs are RELATIVE (the dataProvider already prepends /api — never `${apiUrl}/x`, that doubles).

interface Ref {
  id: number;
  label: string;
}

interface ProductDetailData {
  id: number;
  version: number;
  naam: string;
  omschrijving: string | null;
  artikelCode: string | null;
  eanNummer: string | null;
  leverancierProductNummer: string | null;
  leverancier: Ref | null;
  verkoopEenheid: string | null;
  inkoopEenheid: string | null;
  productGroep: Ref | null;
  productType: string;
  scopeType: string;
  voorraadHuidig: number | null;
  stockMasked: boolean;
  voorraadMinimum: number | null;
  voorraadMaximum: number | null;
  besteleenheid: number | null;
  voorraadbeheer: boolean;
  grootboekRekening: Ref | null;
  grootboekRekeningKorting: Ref | null;
  projectCode: Ref | null;
  kostenPlaats: Ref | null;
  toelichting: string | null;
  eigenschappen: string | null;
}

interface TilePreference {
  tileKey: string;
  visible: boolean;
}
interface TilePreferencesData {
  tiles: TilePreference[];
}

interface Optie {
  id: number;
  label: string;
}
interface OptiesData {
  productGroepen: Optie[];
  leverancierRelaties: Optie[];
  grootboekRekeningen: Optie[];
  projectCodes: Optie[];
  kostenPlaatsen: Optie[];
  btwCodes: Optie[];
  eenheden: string[];
}

// A single price rule (Story 3.3): the shape of GET /api/producten/{id}/prijzen rows — newest-first.
// Faithful to PrijsregelResponse: Vanaf / Verkoop / Inkoop / BTW (id + percentage label).
interface PrijsRegel {
  id: number;
  vanaf: string | null;
  verkoopprijs: number | null;
  inkoopprijs: number | null;
  btwCodeId: number | null;
  btwPercentage: number | null;
}

// A single row of the combined stock timeline (Story 4.3 / FR13): the shape of
// GET /api/producten/{id}/voorraadmutaties rows — newest-first, manual rows ∪ sales write-offs.
// Faithful to VoorraadMutatieRegelResponse: aantal is SIGNED (+ opboeking / − afboeking);
// voorraadNa is only present on manual rows; bron is the HANDMATIG/VERKOOP enum.
interface VoorraadMutatieRegel {
  datum: string | null;
  bron: "HANDMATIG" | "VERKOOP";
  soort: string;
  omschrijving: string | null;
  aantal: number;
  voorraadNa: number | null;
  geboektDoor: string | null;
  factuurId: number | null;
  factuurNummer: number | null;
  factuurRegelId: number | null;
  // Only on sales rows — shown in the Factuurregel detail popup (Jmix `openFactuurregel`).
  regelAantal: number | null;
  stuksPrijs: number | null;
}

// The five manual mutation kinds (VoorraadMutatieType ids); labels resolve via i18n. Sign/direction is
// NOT in the type — it is the `opboeking` flag on the request (faithful to the Jmix dialog).
const MUTATIE_SOORTEN = ["INKOOP", "RETOUR", "CORRECTIE", "AFSCHRIJVING", "OVERIG"] as const;

// The mutable form mirror of the four editable tiles. Numbers live as strings while typing (empty =
// "preserve existing", faithful to the Jmix "empty input preserves NOT-NULL" rule the API enforces);
// association ids live as strings ("" = cleared).
interface EditForm {
  naam: string;
  omschrijving: string;
  productGroepId: string;
  productType: string;
  scopeType: string;
  artikelCode: string;
  leverancierId: string;
  eanNummer: string;
  leverancierProductNummer: string;
  verkoopEenheid: string;
  inkoopEenheid: string;
  voorraadbeheer: boolean;
  voorraadMinimum: string;
  voorraadMaximum: string;
  besteleenheid: string;
  grootboekRekeningId: string;
  grootboekRekeningKortingId: string;
  projectCodeId: string;
  kostenPlaatsId: string;
  eigenschappen: string;
}

const PRODUCT_TYPES = ["VERKOOP", "DIENST", "ENTING", "ONTWORMEN", "MEDICIJN", "APOTHEEK"] as const;
const SCOPE_TYPES = ["ORGANISATIE", "VESTIGING"] as const;

// The six product-detail tiles in display order (Story 5.1 "Tegels" dialog): the exact Jmix tile_key
// literals paired with their numbered label (1 Basisinformatie … 6 Metadata & eigenschappen).
const TILE_KEYS = [
  { key: "BASISINFO", n: 1, label: "productDetailView.tile.basis" },
  { key: "CLASSIFICATIE", n: 2, label: "productDetailView.tile.classificatie" },
  { key: "VOORRAAD", n: 3, label: "productDetailView.tile.voorraad" },
  { key: "PRIJZEN", n: 4, label: "productDetailView.tile.prijzen" },
  { key: "BOEKHOUDING", n: 5, label: "productDetailView.tile.boekhouding" },
  { key: "METADATA", n: 6, label: "productDetailView.tile.metadata" },
] as const;

function toForm(p: ProductDetailData): EditForm {
  const refId = (r: Ref | null) => (r ? String(r.id) : "");
  const numStr = (n: number | null | undefined) => (n == null ? "" : String(n));
  return {
    naam: p.naam ?? "",
    omschrijving: p.omschrijving ?? "",
    productGroepId: refId(p.productGroep),
    productType: p.productType,
    scopeType: p.scopeType,
    artikelCode: p.artikelCode ?? "",
    leverancierId: refId(p.leverancier),
    eanNummer: p.eanNummer ?? "",
    leverancierProductNummer: p.leverancierProductNummer ?? "",
    verkoopEenheid: p.verkoopEenheid ?? "",
    inkoopEenheid: p.inkoopEenheid ?? "",
    voorraadbeheer: p.voorraadbeheer,
    voorraadMinimum: numStr(p.voorraadMinimum),
    voorraadMaximum: numStr(p.voorraadMaximum),
    besteleenheid: numStr(p.besteleenheid),
    grootboekRekeningId: refId(p.grootboekRekening),
    grootboekRekeningKortingId: refId(p.grootboekRekeningKorting),
    projectCodeId: refId(p.projectCode),
    kostenPlaatsId: refId(p.kostenPlaats),
    eigenschappen: p.eigenschappen ?? "",
  };
}

// Build <select> options, guaranteeing the current value is present even before /opties loads (or when
// the stored value falls outside the returned list). `blank` adds a clearable "—" first option.
function selectOptions(
  rows: Optie[],
  currentValue: string,
  currentLabel: string | null,
  blank: boolean,
  noneLabel: string,
): { value: string; label: string }[] {
  const opts = rows.map((r) => ({ value: String(r.id), label: r.label }));
  if (currentValue && !opts.some((o) => o.value === currentValue)) {
    opts.unshift({ value: currentValue, label: currentLabel ?? currentValue });
  }
  return blank ? [{ value: "", label: noneLabel }, ...opts] : opts;
}

// Numbered green circle + title, matching the Jmix tile header (e.g. "① Basisinformatie").
function TileHeading({ n, label }: { n: number; label: string }) {
  return (
    <span className="product-tile-heading">
      <span className="product-tile-heading__num" aria-hidden="true">
        {n}
      </span>
      {label}
    </span>
  );
}

export function ProductDetail({ id, onBack }: { id: string; onBack: () => void }) {
  const t = useTranslate();
  const getLocale = useGetLocale();
  const locale = getLocale() ?? "nl";

  // Classificatie gating (Jmix ProductForm.jsx): Leverancier, Leverancier productnr. and Eenheid
  // inkoop are shown only when the `leverancier` module is enabled (permissive while the feed loads).
  const { isModuleEnabled } = useModuleAvailability();
  const leverancierEnabled = isModuleEnabled("leverancier");

  const { data, isLoading, isError, refetch } = useOne<ProductDetailData>({ resource: "producten", id });
  const { data: prefData } = useCustom<TilePreferencesData>({
    url: "producten/tile-preferences",
    method: "get",
  });
  const { data: optiesData } = useCustom<OptiesData>({ url: "producten/opties", method: "get" });

  // Tab strip (Story 5.3): "Product" is always present; a type-specific config tab appears for ENTING
  // ("Entingen"), ONTWORMEN ("Ontwormen & ontvlooien" — behandeling) and MEDICIJN/APOTHEEK ("Medicijn").
  const [activeTab, setActiveTab] = useState<"product" | "entingen" | "behandeling" | "medicijn">("product");

  // Opens directly editable (Jmix parity). The form is seeded from the loaded product in the effect below;
  // `editing` stays true for an existing product so the fields are writable immediately.
  const [editing, setEditing] = useState(true);
  const [form, setForm] = useState<EditForm | null>(null);
  const [version, setVersion] = useState<number | null>(null);
  const [notice, setNotice] = useState<
    "saved" | "error" | "conflict" | "required" | "metadataInvalid" | "deleted" | "deleteError" | null
  >(null);
  const { mutate, isLoading: saving } = useUpdate();

  // Tegels (tile visibility) dialog (Story 5.1): seeded from the loaded tile-preferences, edited as a
  // draft, then persisted via PUT. `visibilityOverride` holds the live, post-save visibility so the
  // tiles show/hide immediately without re-fetching (the GET prefs query is not re-run).
  const [tegelsOpen, setTegelsOpen] = useState(false);
  const [tegelsDraft, setTegelsDraft] = useState<Record<string, boolean>>({});
  const [visibilityOverride, setVisibilityOverride] = useState<Record<string, boolean> | null>(null);
  const { mutate: mutateTegels, isLoading: tegelsBusy } = useCustomMutation();
  const [tegelsError, setTegelsError] = useState(false);

  // Guarded delete (Story 5.2): a 409 carries the koppelingen message — surfaced verbatim, no navigate.
  const { mutate: mutateDelete, isLoading: deleting } = useCustomMutation();
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);

  // Prijzen tile (Story 3.3): the price rules are their own sub-resource (not part of the product
  // aggregate). All urls are RELATIVE — the dataProvider prepends /api once.
  const {
    data: prijzenData,
    refetch: refetchPrijzen,
  } = useCustom<PrijsRegel[]>({ url: `producten/${id}/prijzen`, method: "get" });
  const { mutate: mutatePrijs, isLoading: prijsBusy } = useCustomMutation();

  // Add-rule dialog state (mirrors the Jmix "Prijsregel toevoegen" dialog, capture 09).
  const [addOpen, setAddOpen] = useState(false);
  const [addVanaf, setAddVanaf] = useState("");
  const [addBtwId, setAddBtwId] = useState("");
  const [addVerkoop, setAddVerkoop] = useState("");
  const [addInkoop, setAddInkoop] = useState("");
  const [addMissing, setAddMissing] = useState(false);
  const [prijsNotice, setPrijsNotice] = useState<"addError" | "removeError" | null>(null);

  // Voorraadmutaties surface (Story 4.3): the combined timeline is its own sub-resource (not part of
  // the product aggregate); only fetched while the dialog is open. All urls RELATIVE (/api added once).
  const [mutatiesOpen, setMutatiesOpen] = useState(false);
  const {
    data: mutatiesData,
    refetch: refetchMutaties,
  } = useCustom<VoorraadMutatieRegel[]>({
    url: `producten/${id}/voorraadmutaties`,
    method: "get",
    queryOptions: { enabled: mutatiesOpen },
  });
  const { mutate: mutateMutatie, isLoading: mutatieBusy } = useCustomMutation();

  // Factuurregel detail popup (Jmix `openFactuurregel`): the sales row whose invoice line is shown.
  const [factuurRegel, setFactuurRegel] = useState<VoorraadMutatieRegel | null>(null);

  // Booking-form state (mirrors the Jmix "Voorraadmutatie boeken" dialog, capture 08).
  const [boekOpen, setBoekOpen] = useState(false);
  const [boekDatum, setBoekDatum] = useState("");
  const [boekOpboeking, setBoekOpboeking] = useState(true);
  const [boekAantal, setBoekAantal] = useState("");
  const [boekSoort, setBoekSoort] = useState<(typeof MUTATIE_SOORTEN)[number]>("INKOOP");
  const [boekReden, setBoekReden] = useState("");
  const [boekMissing, setBoekMissing] = useState(false);
  const [boekError, setBoekError] = useState(false);

  // The displayed current stock — seeded from the aggregate, then re-synced from the POST response so a
  // booking reflects immediately (and the bumped version round-trips so no follow-on 409 on a later PUT).
  const [voorraadHuidig, setVoorraadHuidig] = useState<number | null>(null);
  const [stockMasked, setStockMasked] = useState(false);

  const p = data?.data;

  // (Re)seed the editable mirror whenever the product first loads or a save bumps its version. The screen
  // opens directly editable (Jmix parity), so the form is seeded here rather than behind a "Bewerken" click.
  useEffect(() => {
    if (p) {
      setVersion(p.version ?? null);
      setVoorraadHuidig(p.voorraadHuidig ?? null);
      setStockMasked(p.stockMasked);
      setForm(toForm(p));
      setEditing(true);
    }
  }, [p?.id, p?.version]); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  if (isError || !p) {
    return <p className="fawkes-error">{t("productDetailView.loadFailed")}</p>;
  }

  const opties = optiesData?.data;

  // Tile visibility: a tile is shown unless the user explicitly toggled it off (unset = visible,
  // faithful to ProductTilePreferenceService.getZichtbareTiles). After a Tegels save, `visibilityOverride`
  // takes precedence so the change is live without re-fetching the prefs.
  const prefVisible = (key: string) => {
    if (visibilityOverride) return visibilityOverride[key] ?? true;
    const pref = (prefData?.data.tiles ?? []).find((tile) => tile.tileKey === key);
    return pref ? pref.visible : true;
  };
  const show = (key: string) => prefVisible(key);

  // The type-specific config tab is keyed off the SAVED type (not the edit draft): "Entingen" for ENTING,
  // "Ontwormen & ontvlooien" (behandeling) for ONTWORMEN. At most one is present.
  // The type-specific config tab, hero icon and breadcrumb follow the EDITED type (source ProductForm:
  // setTypeListener fires on every change), so switching Type reveals its tab/icon immediately instead of
  // only after save.
  const effType = editing && form ? form.productType : p.productType;
  const isEnting = effType === "ENTING";
  const isOntwormen = effType === "ONTWORMEN";
  const isMedicijn = effType === "MEDICIJN" || effType === "APOTHEEK";
  const hasTypeTab = isEnting || isOntwormen || isMedicijn;
  const typeTabKey: "entingen" | "behandeling" | "medicijn" =
    isEnting ? "entingen" : isOntwormen ? "behandeling" : "medicijn";
  const typeTabActive = hasTypeTab && activeTab === typeTabKey;

  const typeLabel = t(`productDetailView.productType.${effType}`);
  const scopeLabel = t(`productDetailView.scopeType.${p.scopeType}`);
  const num = (v: number | null | undefined) => (v == null ? "—" : String(v));
  const noneLabel = t("productEditView.select.none");

  // Annuleren: discard unsaved edits (re-seed the form from the last-loaded product) and navigate back,
  // matching the Jmix "Product wijzigen" dialog "Annuleren" (discard + close).
  const cancelEdit = () => {
    setForm(toForm(p));
    setNotice(null);
    onBack();
  };
  const set = <K extends keyof EditForm>(key: K, value: EditForm[K]) => {
    setForm((f) => (f ? { ...f, [key]: value } : f));
    setNotice(null);
  };

  const save = () => {
    if (!form) return;
    if (!form.naam.trim()) {
      setNotice("required");
      return;
    }
    // Metadata & eigenschappen (Story 5.1): validate the JSON is parseable before sending (empty = clear).
    // An inline error blocks the save, matching the Jmix helper-text intent.
    const eigenschappen = form.eigenschappen.trim();
    if (eigenschappen !== "") {
      try {
        JSON.parse(eigenschappen);
      } catch {
        setNotice("metadataInvalid");
        return;
      }
    }
    const numOrNull = (v: string) => (v.trim() === "" ? null : Number(v));
    const idOrNull = (v: string) => (v === "" ? null : Number(v));
    const trimOrNull = (v: string) => (v.trim() === "" ? null : v);
    mutate(
      {
        resource: "producten",
        id,
        values: {
          version,
          // Basisinformatie
          naam: form.naam,
          omschrijving: trimOrNull(form.omschrijving),
          productGroepId: idOrNull(form.productGroepId),
          type: form.productType,
          scope: form.scopeType,
          // Classificatie & codes
          artikelCode: trimOrNull(form.artikelCode),
          leverancierRelatieId: idOrNull(form.leverancierId),
          eanNummer: trimOrNull(form.eanNummer),
          leverancierProductNummer: trimOrNull(form.leverancierProductNummer),
          verkoopEenheid: trimOrNull(form.verkoopEenheid),
          inkoopEenheid: trimOrNull(form.inkoopEenheid),
          // Voorraad (huidig is never sent — it is masked / managed via voorraadmutaties, Epic 4)
          voorraadbeheer: form.voorraadbeheer,
          voorraadMinimum: numOrNull(form.voorraadMinimum),
          voorraadMaximum: numOrNull(form.voorraadMaximum),
          besteleenheid: numOrNull(form.besteleenheid),
          // Boekhouding
          grootboekRekeningId: idOrNull(form.grootboekRekeningId),
          grootboekRekeningKortingId: idOrNull(form.grootboekRekeningKortingId),
          projectCodeId: idOrNull(form.projectCodeId),
          kostenPlaatsId: idOrNull(form.kostenPlaatsId),
          // Metadata & eigenschappen — "" clears the JSON blob (the backend maps "" → null).
          eigenschappen,
        },
      },
      {
        onSuccess: (res) => {
          // Stay editable after save (Jmix parity): re-sync the version and surface "Opgeslagen", then
          // refetch — the seeding effect re-seeds the form from the fresh aggregate. We do NOT drop to a
          // read-only view, so the user can keep editing.
          const updated = res.data as unknown as ProductDetailData;
          setVersion(updated.version ?? null);
          setForm(toForm(updated));
          setNotice("saved");
          refetch();
        },
        onError: (err) => {
          setNotice((err as { statusCode?: number }).statusCode === 409 ? "conflict" : "error");
        },
      },
    );
  };

  const noticeText =
    notice === "saved"
      ? t("productEditView.saved")
      : notice === "conflict"
        ? t("productEditView.saveConflict")
        : notice === "required"
          ? t("productEditView.naamRequired")
          : notice === "metadataInvalid"
            ? t("productDetailView.metadata.invalid")
            : notice === "deleted"
              ? t("productDetailView.delete.done")
              : notice === "deleteError"
                ? t("productDetailView.delete.error")
                : notice === "error"
                  ? t("productEditView.saveError")
                  : null;

  // Prijzen — the rule list is delivered newest-first by the backend; render verbatim (no client sort).
  const prijsRegels: PrijsRegel[] = prijzenData?.data ?? [];

  const openAddPrijs = () => {
    // Vanaf defaults to today (ISO yyyy-MM-dd), faithful to the Jmix dialog default.
    setAddVanaf(todayIso());
    setAddBtwId("");
    setAddVerkoop("");
    setAddInkoop("");
    setAddMissing(false);
    setPrijsNotice(null);
    setAddOpen(true);
  };

  const submitAddPrijs = () => {
    // verkoopprijs + btw are required — no request if missing (inline message).
    if (addVerkoop.trim() === "" || addBtwId === "") {
      setAddMissing(true);
      return;
    }
    mutatePrijs(
      {
        url: `producten/${id}/prijzen`,
        method: "post",
        values: {
          vanaf: addVanaf || null,
          verkoopprijs: Number(addVerkoop),
          inkoopprijs: addInkoop.trim() === "" ? null : Number(addInkoop),
          btwCodeId: Number(addBtwId),
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setAddOpen(false);
          refetchPrijzen();
        },
        onError: () => setPrijsNotice("addError"),
      },
    );
  };

  const removePrijs = (prijsId: number) => {
    // Confirm before breaking the M2M link (matches the app's window.confirm idiom).
    if (!window.confirm(t("productDetailView.prijzen.remove.confirm"))) return;
    setPrijsNotice(null);
    mutatePrijs(
      {
        url: `producten/${id}/prijzen/${prijsId}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => refetchPrijzen(),
        onError: () => setPrijsNotice("removeError"),
      },
    );
  };

  // Voorraadmutaties — the timeline rows arrive newest-first; render verbatim (no client sort).
  const mutaties: VoorraadMutatieRegel[] = mutatiesData?.data ?? [];

  const openMutaties = () => {
    setMutatiesOpen(true);
  };

  const openBoek = () => {
    // Datum defaults to today (ISO yyyy-MM-dd), faithful to the Jmix dialog default.
    setBoekDatum(todayIso());
    setBoekOpboeking(true);
    setBoekAantal("");
    setBoekSoort("INKOOP");
    setBoekReden("");
    setBoekMissing(false);
    setBoekError(false);
    setBoekOpen(true);
  };

  const submitBoek = () => {
    // Aantal is entered positive and must be > 0; the sign is derived server-side from `opboeking`.
    const aantal = Number(boekAantal);
    if (boekAantal.trim() === "" || !Number.isFinite(aantal) || aantal <= 0) {
      setBoekMissing(true);
      return;
    }
    setBoekError(false);
    mutateMutatie(
      {
        url: `producten/${id}/voorraadmutaties`,
        method: "post",
        values: {
          datum: boekDatum || null,
          aantal,
          type: boekSoort,
          opboeking: boekOpboeking,
          reden: boekReden.trim() === "" ? null : boekReden,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          // The POST returns the refreshed product aggregate (new voorraadHuidig + bumped version) so we
          // sync both — the timeline reflects the booking and a later tile PUT won't hit a stale-version 409.
          const updated = res.data as unknown as ProductDetailData;
          if (updated) {
            setVoorraadHuidig(updated.voorraadHuidig ?? null);
            setStockMasked(updated.stockMasked);
            setVersion(updated.version ?? null);
          }
          setBoekOpen(false);
          refetchMutaties();
        },
        onError: () => setBoekError(true),
      },
    );
  };

  // Tegels (tile visibility) dialog (Story 5.1) — open with a draft seeded from the current visibility,
  // toggle, then Opslaan persists all six {tileKey, visible} via PUT and applies them live; Annuleren discards.
  const openTegels = () => {
    const draft: Record<string, boolean> = {};
    for (const tile of TILE_KEYS) draft[tile.key] = prefVisible(tile.key);
    setTegelsDraft(draft);
    setTegelsError(false);
    setTegelsOpen(true);
  };

  const saveTegels = () => {
    const tiles = TILE_KEYS.map((tile) => ({
      tileKey: tile.key,
      visible: tegelsDraft[tile.key] ?? true,
    }));
    setTegelsError(false);
    mutateTegels(
      {
        url: "producten/tile-preferences",
        method: "put",
        values: { tiles },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          // Apply live: build the override map from the saved draft so tiles show/hide immediately.
          const override: Record<string, boolean> = {};
          for (const tile of TILE_KEYS) override[tile.key] = tegelsDraft[tile.key] ?? true;
          setVisibilityOverride(override);
          setTegelsOpen(false);
        },
        onError: () => setTegelsError(true),
      },
    );
  };

  // Guarded delete (Story 5.2) — confirm, then DELETE. 204 → toast + navigate back; 409 → show the
  // server koppelingen message prominently, do NOT navigate; 404/other → generic error.
  const deleteProduct = () => {
    if (!window.confirm(t("productDetailView.delete.confirm"))) return;
    setDeleteMessage(null);
    setNotice(null);
    mutateDelete(
      {
        url: `producten/${id}`,
        method: "delete",
        values: {},
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          setNotice("deleted");
          onBack();
        },
        onError: (err) => {
          const e = err as { statusCode?: number; message?: string };
          if (e.statusCode === 409) {
            // The 409 body is the Jmix-parity koppelingen message — surface it verbatim.
            setDeleteMessage(e.message || t("productDetailView.delete.error"));
            setNotice(null);
          } else {
            setNotice("deleteError");
          }
        },
      },
    );
  };

  const mutatieColumns: Column<VoorraadMutatieRegel>[] = [
    {
      header: t("productDetailView.mutaties.col.datum"),
      cell: (r) => formatDate(r.datum, locale),
    },
    {
      header: t("productDetailView.mutaties.col.bron"),
      cell: (r) => t(`productDetailView.mutaties.bron.${r.bron}`),
    },
    { header: t("productDetailView.mutaties.col.soort"), cell: (r) => r.soort },
    { header: t("productDetailView.mutaties.col.omschrijving"), cell: (r) => r.omschrijving ?? "—" },
    {
      // Signed quantity: explicit +/− so an op-/afboeking reads at a glance (Jmix shows the sign).
      header: t("productDetailView.mutaties.col.aantal"),
      cell: (r) => (
        <span className={r.aantal < 0 ? "product-mutatie-aantal--neg" : "product-mutatie-aantal--pos"}>
          {r.aantal > 0 ? `+${r.aantal}` : String(r.aantal)}
        </span>
      ),
    },
    {
      // Stock-after only exists on manual rows (null on sales) — and is withheld for a masked operator.
      header: t("productDetailView.mutaties.col.voorraadNa"),
      cell: (r) =>
        r.voorraadNa == null ? "—" : stockMasked ? t("productListView.stock.restricted") : String(r.voorraadNa),
    },
    {
      // Trailing action column (Jmix `regelActie`): sales rows carry an invoice-line reference and get an
      // invoice-icon button that opens the Factuurregel detail popup; manual rows render nothing.
      header: "",
      cell: (r) =>
        r.factuurRegelId != null ? (
          <button
            type="button"
            className="product-mutatie-factuurbtn"
            title={t("productDetailView.factuurregel.view")}
            aria-label={t("productDetailView.factuurregel.view")}
            onClick={() => setFactuurRegel(r)}
          >
            <InvoiceIcon width={16} height={16} />
          </button>
        ) : null,
    },
  ];

  const prijsColumns: Column<PrijsRegel>[] = [
    { header: t("productDetailView.prijzen.vanaf"), cell: (r) => formatDate(r.vanaf, locale) },
    {
      header: t("productDetailView.prijzen.verkoop"),
      cell: (r) => formatEuro(r.verkoopprijs, locale),
    },
    {
      header: t("productDetailView.prijzen.inkoop"),
      cell: (r) => formatEuro(r.inkoopprijs, locale),
    },
    {
      header: t("productDetailView.prijzen.btw"),
      cell: (r) => (r.btwPercentage == null ? "—" : `${r.btwPercentage}%`),
    },
    {
      // Per-row delete (trash icon). No inline edit of existing rules (M2M shared-rule safety) —
      // only add and remove. ROLE_PRODUCT_EDIT is enforced server-side; the affordance stays enabled
      // and a 403 surfaces as the remove-error notice.
      header: "",
      cell: (r) => (
        <button
          type="button"
          className="product-prijs-remove"
          onClick={() => removePrijs(r.id)}
          disabled={prijsBusy}
          aria-label={t("productDetailView.prijzen.remove")}
          title={t("productDetailView.prijzen.remove")}
        >
          <TrashIcon width={16} height={16} aria-hidden="true" />
        </button>
      ),
    },
  ];

  // A typed <select> bound to an association id (used by the editable tiles).
  const RefSelect = ({
    value,
    rows,
    currentLabel,
    onChange,
  }: {
    value: string;
    rows: Optie[];
    currentLabel: string | null;
    onChange: (v: string) => void;
  }) => (
    <select
      className="fui-field__input fui-select"
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {selectOptions(rows, value, currentLabel, true, noneLabel).map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );

  return (
    <FormCard>
      {/* Identiteitskaart header: icon + naam + "Onderdeel › Type" (Jmix capture 02). Icon + breadcrumb
          follow the EDITED type/group (Jmix ProductForm heroIcon: 💉 ENTING, 💊 MEDICIJN/APOTHEEK,
          🪱 ONTWORMEN, 📦 otherwise); the onderdeel falls back to "Algemeen" when there is no group. */}
      <div className="product-identity">
        <span className="product-identity__icon" aria-hidden="true">
          {isEnting ? "💉" : isMedicijn ? "💊" : isOntwormen ? "🪱" : "📦"}
        </span>
        <div className="product-identity__text">
          <h3 className="product-identity__naam">{p.naam}</h3>
          <p className="product-identity__sub">
            <span className="product-identity__onderdeel">
              {(editing && form
                ? opties?.productGroepen?.find((g) => String(g.id) === form.productGroepId)?.label
                : p.productGroep?.label) ?? t("productDetailView.onderdeel.fallback")}
            </span>
            <span className="product-identity__sep" aria-hidden="true">
              ›
            </span>
            <span>{typeLabel}</span>
          </p>
        </div>
        {/* "Tegels" control (Story 5.1, Jmix capture 06): opens the tile-visibility dialog. */}
        <Button variant="secondary" onClick={openTegels}>
          {t("productDetailView.tegels")}
        </Button>
      </div>

      {/* Tab strip (Story 5.3, Jmix capture 10): "Product" always; a type-specific config tab for ENTING
          ("Entingen"), ONTWORMEN ("Ontwormen & ontvlooien") or MEDICIJN/APOTHEEK ("Medicijn"). Matches the
          in-dialog top tabs of the Jmix ProductDetailDialog. */}
      {hasTypeTab && (
        <div className="product-tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "product"}
            className={`product-tab${activeTab === "product" ? " product-tab--active" : ""}`}
            onClick={() => setActiveTab("product")}
          >
            {t("productDetailView.tab.product")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={typeTabActive}
            className={`product-tab${typeTabActive ? " product-tab--active" : ""}`}
            onClick={() => setActiveTab(typeTabKey)}
          >
            {isEnting
              ? t("productDetailView.tab.entingen")
              : isOntwormen
                ? t("productDetailView.tab.behandeling")
                : t("productDetailView.tab.medicijn")}
          </button>
        </div>
      )}

      {typeTabActive &&
        (isEnting ? <EntingenTab id={id} /> : isOntwormen ? <BehandelingTab id={id} /> : <MedicijnTab id={id} />)}

      <div className="product-tiles" style={typeTabActive ? { display: "none" } : undefined}>
        {/* 1 — Basisinformatie */}
        {show("BASISINFO") && (
          <Tile title={<TileHeading n={1} label={t("productDetailView.tile.basis")} />}>
            {editing && form ? (
              <div className="fawkes-form-grid">
                <FieldShell label={t("productDetailView.basis.naam")} required>
                  <input
                    className="fui-field__input"
                    value={form.naam}
                    onChange={(e) => set("naam", e.target.value)}
                  />
                </FieldShell>
                <FieldShell label={t("productDetailView.basis.onderdeel")}>
                  <RefSelect
                    value={form.productGroepId}
                    rows={opties?.productGroepen ?? []}
                    currentLabel={p.productGroep?.label ?? null}
                    onChange={(v) => set("productGroepId", v)}
                  />
                </FieldShell>
                <FieldShell label={t("productDetailView.basis.type")}>
                  <div className="product-radio-group" role="radiogroup">
                    {PRODUCT_TYPES.map((opt) => (
                      <label key={opt} className="product-radio">
                        <input
                          type="radio"
                          name="productType"
                          value={opt}
                          checked={form.productType === opt}
                          onChange={() => set("productType", opt)}
                        />
                        {t(`productDetailView.productType.${opt}`)}
                      </label>
                    ))}
                  </div>
                </FieldShell>
                <FieldShell label={t("productDetailView.basis.scope")}>
                  <div className="product-radio-group" role="radiogroup">
                    {SCOPE_TYPES.map((opt) => (
                      <label key={opt} className="product-radio">
                        <input
                          type="radio"
                          name="scopeType"
                          value={opt}
                          checked={form.scopeType === opt}
                          onChange={() => set("scopeType", opt)}
                        />
                        {t(`productDetailView.scopeType.${opt}`)}
                      </label>
                    ))}
                  </div>
                </FieldShell>
                <FieldShell label={t("productDetailView.basis.omschrijving")} span2>
                  <textarea
                    className="fui-field__input"
                    rows={2}
                    value={form.omschrijving}
                    onChange={(e) => set("omschrijving", e.target.value)}
                  />
                </FieldShell>
              </div>
            ) : (
              <div className="fawkes-form-grid">
                <ReadField label={t("productDetailView.basis.naam")} value={p.naam} />
                <ReadField
                  label={t("productDetailView.basis.onderdeel")}
                  value={p.productGroep?.label ?? null}
                />
                <ReadField label={t("productDetailView.basis.type")} value={typeLabel} />
                <ReadField label={t("productDetailView.basis.scope")} value={scopeLabel} />
                <ReadField
                  label={t("productDetailView.basis.omschrijving")}
                  value={p.omschrijving}
                  span2
                />
              </div>
            )}
          </Tile>
        )}

        {/* 2 — Classificatie & codes */}
        {show("CLASSIFICATIE") && (
          <Tile title={<TileHeading n={2} label={t("productDetailView.tile.classificatie")} />}>
            {editing && form ? (
              <div className="fawkes-form-grid">
                <FieldShell label={t("productDetailView.classificatie.artikelCode")}>
                  <input
                    className="fui-field__input"
                    value={form.artikelCode}
                    onChange={(e) => set("artikelCode", e.target.value)}
                  />
                </FieldShell>
                {leverancierEnabled && (
                  <FieldShell label={t("productDetailView.classificatie.leverancier")}>
                    <RefSelect
                      value={form.leverancierId}
                      rows={opties?.leverancierRelaties ?? []}
                      currentLabel={p.leverancier?.label ?? null}
                      onChange={(v) => set("leverancierId", v)}
                    />
                  </FieldShell>
                )}
                <FieldShell label={t("productDetailView.classificatie.ean")}>
                  <input
                    className="fui-field__input"
                    placeholder={t("productDetailView.classificatie.eanPlaceholder")}
                    value={form.eanNummer}
                    onChange={(e) => set("eanNummer", e.target.value)}
                  />
                </FieldShell>
                {leverancierEnabled && (
                  <FieldShell label={t("productDetailView.classificatie.leverancierProductNr")}>
                    <input
                      className="fui-field__input"
                      placeholder={t("productDetailView.classificatie.leverancierProductNrPlaceholder")}
                      value={form.leverancierProductNummer}
                      onChange={(e) => set("leverancierProductNummer", e.target.value)}
                    />
                  </FieldShell>
                )}
                <FieldShell label={t("productDetailView.classificatie.verkoopeenheid")}>
                  <select
                    className="fui-field__input fui-select"
                    value={form.verkoopEenheid}
                    onChange={(e) => set("verkoopEenheid", e.target.value)}
                  >
                    <option value="">{t("productDetailView.classificatie.eenheidPlaceholder")}</option>
                    {(opties?.eenheden ?? []).map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                    {form.verkoopEenheid && !(opties?.eenheden ?? []).includes(form.verkoopEenheid) && (
                      <option value={form.verkoopEenheid}>{form.verkoopEenheid}</option>
                    )}
                  </select>
                </FieldShell>
                {leverancierEnabled && (
                  <FieldShell label={t("productDetailView.classificatie.inkoopeenheid")}>
                    <select
                      className="fui-field__input fui-select"
                      value={form.inkoopEenheid}
                      onChange={(e) => set("inkoopEenheid", e.target.value)}
                    >
                      <option value="">{t("productDetailView.classificatie.eenheidPlaceholder")}</option>
                      {(opties?.eenheden ?? []).map((u) => (
                        <option key={u} value={u}>
                          {u}
                        </option>
                      ))}
                      {form.inkoopEenheid && !(opties?.eenheden ?? []).includes(form.inkoopEenheid) && (
                        <option value={form.inkoopEenheid}>{form.inkoopEenheid}</option>
                      )}
                    </select>
                  </FieldShell>
                )}
              </div>
            ) : (
              <div className="fawkes-form-grid">
                <ReadField
                  label={t("productDetailView.classificatie.artikelCode")}
                  value={p.artikelCode}
                />
                {leverancierEnabled && (
                  <ReadField
                    label={t("productDetailView.classificatie.leverancier")}
                    value={p.leverancier?.label ?? null}
                  />
                )}
                <ReadField label={t("productDetailView.classificatie.ean")} value={p.eanNummer} />
                {leverancierEnabled && (
                  <ReadField
                    label={t("productDetailView.classificatie.leverancierProductNr")}
                    value={p.leverancierProductNummer}
                  />
                )}
                <ReadField
                  label={t("productDetailView.classificatie.verkoopeenheid")}
                  value={p.verkoopEenheid}
                />
                {leverancierEnabled && (
                  <ReadField
                    label={t("productDetailView.classificatie.inkoopeenheid")}
                    value={p.inkoopEenheid}
                  />
                )}
              </div>
            )}
          </Tile>
        )}

        {/* 3 — Voorraad. Hidden entirely for DIENST products (Jmix ProductForm.jsx:
            heeft("VOORRAAD") && f.productType !== "DIENST") — a service has no stock. */}
        {show("VOORRAAD") && effType !== "DIENST" && (
          <Tile title={<TileHeading n={3} label={t("productDetailView.tile.voorraad")} />}>
            {editing && form ? (
              <div className="fawkes-form-grid">
                <FieldShell label={t("productDetailView.voorraad.beheer")} span2>
                  <label className="fui-toggle product-toggle">
                    <input
                      type="checkbox"
                      role="switch"
                      className="fui-toggle__input"
                      checked={form.voorraadbeheer}
                      aria-label={t("productDetailView.voorraad.beheer")}
                      onChange={(e) => set("voorraadbeheer", e.target.checked)}
                    />
                    <span className="fui-toggle__track" aria-hidden="true">
                      <span className="fui-toggle__thumb" />
                    </span>
                  </label>
                </FieldShell>
                <FieldShell label={t("productDetailView.voorraad.minimum")}>
                  <input
                    type="number"
                    className="fui-field__input"
                    value={form.voorraadMinimum}
                    onChange={(e) => set("voorraadMinimum", e.target.value)}
                  />
                </FieldShell>
                <FieldShell label={t("productDetailView.voorraad.maximum")}>
                  <input
                    type="number"
                    className="fui-field__input"
                    value={form.voorraadMaximum}
                    onChange={(e) => set("voorraadMaximum", e.target.value)}
                  />
                </FieldShell>
                {/* Current stock stays read-only even in edit mode — it is field-masked / managed via
                    stock movements (Epic 4); a masked operator must never be able to write it. */}
                <FieldShell label={t("productDetailView.voorraad.huidig")}>
                  <span className="product-readonly-value">
                    {stockMasked
                      ? t("productListView.stock.restricted")
                      : num(voorraadHuidig)}
                  </span>
                </FieldShell>
                <FieldShell label={t("productDetailView.voorraad.besteleenheid")}>
                  <input
                    type="number"
                    className="fui-field__input"
                    value={form.besteleenheid}
                    onChange={(e) => set("besteleenheid", e.target.value)}
                  />
                </FieldShell>
              </div>
            ) : (
              <div className="fawkes-form-grid">
                <ReadField
                  label={t("productDetailView.voorraad.beheer")}
                  value={
                    p.voorraadbeheer
                      ? t("productDetailView.voorraad.beheer.on")
                      : t("productDetailView.voorraad.beheer.off")
                  }
                  span2
                />
                <ReadField
                  label={t("productDetailView.voorraad.minimum")}
                  value={num(p.voorraadMinimum)}
                />
                <ReadField
                  label={t("productDetailView.voorraad.maximum")}
                  value={num(p.voorraadMaximum)}
                />
                {/* Current stock is field-masked unless ROLE_PRODUCT_STOCK_VIEW (stockMasked=true). */}
                <ReadField
                  label={t("productDetailView.voorraad.huidig")}
                  value={stockMasked ? t("productListView.stock.restricted") : num(voorraadHuidig)}
                />
                <ReadField
                  label={t("productDetailView.voorraad.besteleenheid")}
                  value={num(p.besteleenheid)}
                />
              </div>
            )}
            {/* "↗ Mutaties" opens the stock-movement timeline + booking surface (Story 4.3). The timeline
                aantal columns are operational data, so it stays viewable even for a stock-masked operator
                (only the "Huidige voorraad" number stays withheld). ROLE_PRODUCT_EDIT is enforced
                server-side on the booking POST (→ 403, surfaced inline); the affordance stays enabled. */}
            <button type="button" className="product-prijs-add" onClick={openMutaties}>
              {t("productDetailView.voorraad.mutaties")}
            </button>
          </Tile>
        )}

        {/* 4 — Prijzen (list + add + remove; no inline edit — M2M shared-rule safety, Story 3.3) */}
        {show("PRIJZEN") && (
          <Tile title={<TileHeading n={4} label={t("productDetailView.tile.prijzen")} />}>
            <IndeelbareTabel tabelKey="product-detail-prijzen"
              columns={prijsColumns}
              rows={prijsRegels}
              getRowKey={(r) => String(r.id)}
              empty={t("productDetailView.prijzen.empty")}
            />
            {prijsNotice === "removeError" && (
              <p className="product-notice product-notice--error product-prijs-error" role="status">
                {t("productDetailView.prijzen.remove.error")}
              </p>
            )}
            {/* ROLE_PRODUCT_EDIT is enforced server-side (POST → 403); no client authority signal is
                surfaced, so the affordance stays enabled and a 403 surfaces as the add-error notice. */}
            <button type="button" className="product-prijs-add" onClick={openAddPrijs}>
              <PlusCircleSolidIcon width={18} height={18} /> {t("productDetailView.prijzen.add")}
            </button>
          </Tile>
        )}

        {/* 5 — Boekhouding (Geavanceerd badge) */}
        {show("BOEKHOUDING") && (
          <Tile
            title={<TileHeading n={5} label={t("productDetailView.tile.boekhouding")} />}
            actions={<StatusBadge tone="neutral">{t("productDetailView.boekhouding.advanced")}</StatusBadge>}
          >
            {editing && form ? (
              <div className="fawkes-form-grid">
                <FieldShell label={t("productDetailView.boekhouding.grootboekrekening")}>
                  <RefSelect
                    value={form.grootboekRekeningId}
                    rows={opties?.grootboekRekeningen ?? []}
                    currentLabel={p.grootboekRekening?.label ?? null}
                    onChange={(v) => set("grootboekRekeningId", v)}
                  />
                </FieldShell>
                <FieldShell label={t("productDetailView.boekhouding.projectcode")}>
                  <RefSelect
                    value={form.projectCodeId}
                    rows={opties?.projectCodes ?? []}
                    currentLabel={p.projectCode?.label ?? null}
                    onChange={(v) => set("projectCodeId", v)}
                  />
                </FieldShell>
                <FieldShell label={t("productDetailView.boekhouding.grootboekrekeningKorting")}>
                  <RefSelect
                    value={form.grootboekRekeningKortingId}
                    rows={opties?.grootboekRekeningen ?? []}
                    currentLabel={p.grootboekRekeningKorting?.label ?? null}
                    onChange={(v) => set("grootboekRekeningKortingId", v)}
                  />
                </FieldShell>
                <FieldShell label={t("productDetailView.boekhouding.kostenplaats")}>
                  <RefSelect
                    value={form.kostenPlaatsId}
                    rows={opties?.kostenPlaatsen ?? []}
                    currentLabel={p.kostenPlaats?.label ?? null}
                    onChange={(v) => set("kostenPlaatsId", v)}
                  />
                </FieldShell>
              </div>
            ) : (
              <div className="fawkes-form-grid">
                <ReadField
                  label={t("productDetailView.boekhouding.grootboekrekening")}
                  value={p.grootboekRekening?.label ?? null}
                />
                <ReadField
                  label={t("productDetailView.boekhouding.projectcode")}
                  value={p.projectCode?.label ?? null}
                />
                <ReadField
                  label={t("productDetailView.boekhouding.grootboekrekeningKorting")}
                  value={p.grootboekRekeningKorting?.label ?? null}
                />
                <ReadField
                  label={t("productDetailView.boekhouding.kostenplaats")}
                  value={p.kostenPlaats?.label ?? null}
                />
              </div>
            )}
          </Tile>
        )}

        {/* 6 — Metadata & eigenschappen (editable JSON in screen edit mode, Story 5.1) */}
        {show("METADATA") && (
          <Tile title={<TileHeading n={6} label={t("productDetailView.tile.metadata")} />}>
            {editing && form ? (
              <div className="fawkes-form-grid">
                <FieldShell label={t("productDetailView.metadata.json")} span2>
                  <textarea
                    className="fui-field__input product-metadata-input"
                    rows={6}
                    placeholder={t("productDetailView.metadata.jsonPlaceholder")}
                    value={form.eigenschappen}
                    onChange={(e) => set("eigenschappen", e.target.value)}
                    aria-invalid={notice === "metadataInvalid"}
                  />
                </FieldShell>
              </div>
            ) : (
              <ReadField
                label={t("productDetailView.metadata.json")}
                value={p.eigenschappen}
                span2
              />
            )}
            <p className="fawkes-muted product-metadata-helper">
              {t("productDetailView.metadata.helper")}
            </p>
          </Tile>
        )}
      </div>

      {/* Guarded-delete 409 koppelingen message (Story 5.2) — surfaced prominently above the footer; the
          user stays on the screen so they can resolve the references first. */}
      {deleteMessage && activeTab === "product" && (
        <p className="product-notice product-notice--error product-delete-error" role="alert">
          {deleteMessage}
        </p>
      )}

      {/* The screen-level footer drives the Product tab; the Entingen tab owns its own footer (Story 5.3). */}
      {activeTab === "product" && (
      <ActionBar>
        {noticeText && (
          <span className={`product-notice product-notice--${notice}`} role="status">
            {noticeText}
          </span>
        )}
        {/* The screen opens directly editable (Jmix "Product wijzigen" parity): the footer is always
            Verwijderen · Annuleren · Opslaan. ROLE_PRODUCT_EDIT is enforced server-side (PUT/DELETE →
            403); no client authority signal is surfaced, so the affordances stay enabled and a 403
            surfaces as a save / delete error. */}
        {/* Verwijderen — bottom-left, red (Jmix footer, captures 02–05). The left-push keeps it apart
            from Annuleren/Opslaan. */}
        <Button
          variant="secondary"
          className="product-delete-button"
          onClick={deleteProduct}
          disabled={deleting || saving}
        >
          <TrashIcon width={16} height={16} aria-hidden="true" />
          {t("productDetailView.delete")}
        </Button>
        {/* Annuleren reverts unsaved edits and navigates back (Jmix dialog cancel = discard + close). */}
        <Button variant="secondary" onClick={cancelEdit} disabled={saving}>
          {t("productEditView.cancel")}
        </Button>
        <Button variant="primary" onClick={save} disabled={saving || !form}>
          {t("productEditView.save")}
        </Button>
      </ActionBar>
      )}

      {/* Add-a-price-rule dialog (Jmix "Prijsregel toevoegen", capture 09): Vanaf[date, default today],
          BTW[dropdown, required], Verkoopprijs[required], Inkoopprijs[optional]. */}
      <Modal
        open={addOpen}
        title={t("productDetailView.prijzen.dialog.title")}
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setAddOpen(false)} disabled={prijsBusy}>
              {t("productEditView.cancel")}
            </Button>
            <Button variant="primary" onClick={submitAddPrijs} disabled={prijsBusy}>
              {t("productDetailView.prijzen.dialog.submit")}
            </Button>
          </>
        }
      >
        <div className="fawkes-form-grid">
          <FieldShell label={t("productDetailView.prijzen.dialog.vanaf")}>
            <DateField className="fui-field__input"
              value={addVanaf}
              onChange={(e) => setAddVanaf(e.target.value)}
            />
          </FieldShell>
          <FieldShell label={t("productDetailView.prijzen.dialog.btw")} required>
            <select
              className="fui-field__input fui-select"
              value={addBtwId}
              onChange={(e) => {
                setAddBtwId(e.target.value);
                setAddMissing(false);
                setPrijsNotice(null);
              }}
            >
              <option value="">{noneLabel}</option>
              {(opties?.btwCodes ?? []).map((b) => (
                <option key={b.id} value={String(b.id)}>
                  {b.label}
                </option>
              ))}
            </select>
          </FieldShell>
          <FieldShell label={t("productDetailView.prijzen.dialog.verkoop")} required>
            <input
              type="number"
              step="0.01"
              className="fui-field__input"
              value={addVerkoop}
              onChange={(e) => {
                setAddVerkoop(e.target.value);
                setAddMissing(false);
                setPrijsNotice(null);
              }}
            />
          </FieldShell>
          <FieldShell
            label={`${t("productDetailView.prijzen.dialog.inkoop")} ${t("productDetailView.prijzen.dialog.inkoop.optional")}`}
          >
            <input
              type="number"
              step="0.01"
              className="fui-field__input"
              value={addInkoop}
              onChange={(e) => setAddInkoop(e.target.value)}
            />
          </FieldShell>
        </div>
        {addMissing && (
          <p className="product-notice product-notice--required" role="status">
            {t("productDetailView.prijzen.dialog.required")}
          </p>
        )}
        {prijsNotice === "addError" && (
          <p className="product-notice product-notice--error" role="status">
            {t("productDetailView.prijzen.dialog.error")}
          </p>
        )}
      </Modal>

      {/* Voorraadmutaties timeline (Jmix "Voorraadmutaties — <product>", capture 07): current stock,
          "+ Mutatie boeken", the timeline grid, and a "Sluiten" footer. */}
      <Modal
        open={mutatiesOpen}
        title={t("productDetailView.mutaties.dialog.title", { args: [p.naam] })}
        onClose={() => setMutatiesOpen(false)}
        footer={
          <Button variant="secondary" onClick={() => setMutatiesOpen(false)}>
            {t("productDetailView.mutaties.close")}
          </Button>
        }
      >
        <div className="product-mutaties-head">
          <span className="product-mutaties-huidig">
            {t("productDetailView.mutaties.huidig")}{" "}
            <strong>
              {stockMasked ? t("productListView.stock.restricted") : num(voorraadHuidig)}
            </strong>
          </span>
          {/* ROLE_PRODUCT_EDIT is enforced server-side (POST → 403); affordance stays enabled. */}
          <button type="button" className="product-prijs-add" onClick={openBoek}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("productDetailView.mutaties.book")}
          </button>
        </div>
        <IndeelbareTabel tabelKey="product-detail-mutaties"
          columns={mutatieColumns}
          rows={mutaties}
          getRowKey={(r) =>
            // No stable server id on a combined-timeline row; compose a key from the row's fields
            // (a sales row carries factuurRegelId, a manual row is unique by datum+soort+aantal).
            r.factuurRegelId != null
              ? `v-${r.factuurRegelId}`
              : `m-${r.datum ?? ""}-${r.soort}-${r.aantal}-${r.voorraadNa ?? ""}`
          }
          empty={t("productDetailView.mutaties.empty")}
        />
      </Modal>

      {/* Factuurregel detail popup (Jmix `openFactuurregel`): a small read-only dialog opened from a
          sales row's invoice-icon action, listing the underlying invoice-line fields. */}
      <Modal
        open={factuurRegel != null}
        title={t("productDetailView.factuurregel.title")}
        onClose={() => setFactuurRegel(null)}
        footer={
          <Button variant="secondary" onClick={() => setFactuurRegel(null)}>
            {t("productDetailView.factuurregel.close")}
          </Button>
        }
      >
        {factuurRegel && (
          <div className="product-factuurregel">
            {[
              {
                label: t("productDetailView.factuurregel.nummer"),
                value: factuurRegel.factuurNummer == null ? "—" : String(factuurRegel.factuurNummer),
              },
              { label: t("productDetailView.factuurregel.datum"), value: formatDate(factuurRegel.datum, locale) || "—" },
              { label: t("productDetailView.factuurregel.soort"), value: factuurRegel.soort || "—" },
              { label: t("productDetailView.factuurregel.product"), value: factuurRegel.omschrijving || "—" },
              {
                label: t("productDetailView.factuurregel.regelAantal"),
                value: factuurRegel.regelAantal == null ? "—" : num(factuurRegel.regelAantal),
              },
              {
                label: t("productDetailView.factuurregel.stuksPrijs"),
                value: factuurRegel.stuksPrijs == null ? "—" : formatEuro(factuurRegel.stuksPrijs, locale),
              },
            ].map((rij) => (
              <div className="product-factuurregel__rij" key={rij.label}>
                <span className="product-factuurregel__label">{rij.label}</span>
                <span className="product-factuurregel__waarde">{rij.value}</span>
              </div>
            ))}
            <div className="product-factuurregel__rij">
              <span className="product-factuurregel__label">{t("productDetailView.factuurregel.effect")}</span>
              <span
                className={`product-factuurregel__waarde ${
                  factuurRegel.aantal < 0 ? "product-mutatie-aantal--neg" : "product-mutatie-aantal--pos"
                }`}
              >
                {factuurRegel.aantal > 0 ? `+${factuurRegel.aantal}` : String(factuurRegel.aantal)}
              </span>
            </div>
          </div>
        )}
      </Modal>

      {/* Voorraadmutatie boeken form (Jmix dialog, capture 08): Datum, Richting (Opboeking/Afboeking
          radios), Aantal[required>0], Soort[select], Reden/toelichting, Annuleren/Boeken. */}
      <Modal
        open={boekOpen}
        title={t("productDetailView.mutaties.book.title")}
        onClose={() => setBoekOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setBoekOpen(false)} disabled={mutatieBusy}>
              {t("productEditView.cancel")}
            </Button>
            <Button variant="primary" onClick={submitBoek} disabled={mutatieBusy}>
              {t("productDetailView.mutaties.book.submit")}
            </Button>
          </>
        }
      >
        <div className="fawkes-form-grid">
          <FieldShell label={t("productDetailView.mutaties.book.datum")} span2>
            <DateField className="fui-field__input"
              value={boekDatum}
              onChange={(e) => setBoekDatum(e.target.value)}
            />
          </FieldShell>
          <FieldShell label={t("productDetailView.mutaties.book.richting")} span2>
            <div className="product-radio-group" role="radiogroup">
              <label className="product-radio">
                <input
                  type="radio"
                  name="boekRichting"
                  checked={boekOpboeking}
                  onChange={() => setBoekOpboeking(true)}
                />
                {t("productDetailView.mutaties.book.opboeking")}
              </label>
              <label className="product-radio">
                <input
                  type="radio"
                  name="boekRichting"
                  checked={!boekOpboeking}
                  onChange={() => setBoekOpboeking(false)}
                />
                {t("productDetailView.mutaties.book.afboeking")}
              </label>
            </div>
          </FieldShell>
          <FieldShell label={t("productDetailView.mutaties.book.aantal")} required span2>
            <input
              type="number"
              step="0.01"
              min="0"
              className="fui-field__input"
              value={boekAantal}
              onChange={(e) => {
                setBoekAantal(e.target.value);
                setBoekMissing(false);
                setBoekError(false);
              }}
            />
          </FieldShell>
          <FieldShell label={t("productDetailView.mutaties.book.soort")} span2>
            <select
              className="fui-field__input fui-select"
              value={boekSoort}
              onChange={(e) => setBoekSoort(e.target.value as (typeof MUTATIE_SOORTEN)[number])}
            >
              {MUTATIE_SOORTEN.map((s) => (
                <option key={s} value={s}>
                  {t(`productDetailView.mutaties.soort.${s}`)}
                </option>
              ))}
            </select>
          </FieldShell>
          <FieldShell label={t("productDetailView.mutaties.book.reden")} span2>
            <textarea
              className="fui-field__input"
              rows={2}
              value={boekReden}
              onChange={(e) => setBoekReden(e.target.value)}
            />
          </FieldShell>
        </div>
        {boekMissing && (
          <p className="product-notice product-notice--required" role="status">
            {t("productDetailView.mutaties.book.required")}
          </p>
        )}
        {boekError && (
          <p className="product-notice product-notice--error" role="status">
            {t("productDetailView.mutaties.book.error")}
          </p>
        )}
      </Modal>

      {/* Tegels tonen / verbergen (Jmix capture 06): six toggles "1. Basisinformatie" … "6. Metadata &
          eigenschappen", Annuleren / Opslaan. Opslaan PUTs all six and applies the new visibility live. */}
      <Modal
        open={tegelsOpen}
        title={t("productDetailView.tegels.title")}
        onClose={() => setTegelsOpen(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setTegelsOpen(false)} disabled={tegelsBusy}>
              {t("productEditView.cancel")}
            </Button>
            <Button variant="primary" onClick={saveTegels} disabled={tegelsBusy}>
              {t("productEditView.save")}
            </Button>
          </>
        }
      >
        <div className="product-tegels-list">
          {TILE_KEYS.map((tile) => (
            <label key={tile.key} className="fui-toggle product-tegels-row">
              <span className="product-tegels-label">
                {tile.n}. {t(tile.label)}
              </span>
              <input
                type="checkbox"
                role="switch"
                className="fui-toggle__input"
                checked={tegelsDraft[tile.key] ?? true}
                aria-label={`${tile.n}. ${t(tile.label)}`}
                onChange={(e) =>
                  setTegelsDraft((d) => ({ ...d, [tile.key]: e.target.checked }))
                }
              />
              <span className="fui-toggle__track" aria-hidden="true">
                <span className="fui-toggle__thumb" />
              </span>
            </label>
          ))}
        </div>
        {tegelsError && (
          <p className="product-notice product-notice--error" role="status">
            {t("productDetailView.tegels.error")}
          </p>
        )}
      </Modal>
    </FormCard>
  );
}
